import { useEffect, useMemo } from 'react';
import { skipToken, useQuery } from '@apollo/client/react';

import {
    IPortfolioSkillGroupsQueryVariables,
    ISkillGroupFragment,
    PortfolioSkillGroupsDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';

interface IProps {
    variables: IPortfolioSkillGroupsQueryVariables;
    queryOptions?: useQuery.Options;
    skip?: boolean;
}

export const useSkillGroupsList = ({ variables, skip }: IProps) => {
    const { showApolloError } = useApolloError();

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    const queryVariables = useMemo(
        () => ({
            ...variables,
            ...paginationVariables,
        }),
        [variables, paginationVariables]
    );

    const { data, loading, error } = useQuery(
        PortfolioSkillGroupsDocument,
        !skip
            ? {
                  variables: queryVariables,
              }
            : skipToken
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const skillGroupsObject = data?.paginatedGroups;
    const skillGroupsCount = skillGroupsObject?.count;
    const skillGroups = useMemo(
        () =>
            (skillGroupsObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as ISkillGroupFragment[]) || [],
        [skillGroupsObject?.edges]
    );

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(skillGroupsCount || 0);
    }, [skillGroupsCount, loading, initializePagination]);

    return {
        skillGroups,
        skillGroupsCount,
        loading,
        paginationSettings,
        setPage,
    };
};
